import { Platform } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import type { LibroIvaCompraRow } from '../domain/libroIvaComprasTxt';
import { libroIvaCompraCsvAmounts } from '../domain/libroIvaComprasCsv';
import type { LibroIvaVentaRow } from '../domain/libroIvaTxt';
import {
  escapeFiscalHtml,
  formatComprobanteNumber,
  formatFiscalCuit,
  formatFiscalMoney,
} from './fiscalComprobanteFormat';
import type { LibroIvaControlPdfInput } from './libroIvaControlPdfWeb';

function downloadBlobWeb(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function shareNativePdf(uri: string, dialogTitle: string): Promise<void> {
  const canShare = await Sharing.isAvailableAsync();
  if (!canShare) {
    throw new Error('No hay una app disponible para guardar o compartir el PDF en este dispositivo.');
  }
  await Sharing.shareAsync(uri, {
    mimeType: 'application/pdf',
    dialogTitle,
    UTI: 'com.adobe.pdf',
  });
}

function ventasTableHtml(rows: LibroIvaVentaRow[]): string {
  if (rows.length === 0) {
    return '<p>Sin comprobantes de ventas en el período.</p>';
  }
  const body = rows
    .map(
      (r) =>
        `<tr><td>${escapeFiscalHtml(r.issuedAt.slice(0, 10))}</td><td>${r.invoiceType}</td>` +
        `<td>${escapeFiscalHtml(formatComprobanteNumber(r.pointOfSale, r.invoiceNumber) ?? '')}</td>` +
        `<td>${escapeFiscalHtml(r.buyerCuit ?? '')}</td><td>${escapeFiscalHtml(r.buyerName ?? '')}</td>` +
        `<td>${escapeFiscalHtml(formatFiscalMoney(r.netAmount))}</td>` +
        `<td>${escapeFiscalHtml(formatFiscalMoney(r.totalAmount))}</td></tr>`
    )
    .join('');
  return `<table border="1" cellpadding="4" cellspacing="0" width="100%" style="font-size:11px;border-collapse:collapse">
    <thead><tr><th>Fecha</th><th>Tipo</th><th>Cbte</th><th>CUIT</th><th>Cliente</th><th>Neto</th><th>Total</th></tr></thead>
    <tbody>${body}</tbody></table>`;
}

function comprasTableHtml(rows: LibroIvaCompraRow[]): string {
  if (rows.length === 0) {
    return '<p>Sin comprobantes de compras con datos fiscales.</p>';
  }
  const body = rows
    .map((r) => {
      const a = libroIvaCompraCsvAmounts(r);
      return (
        `<tr><td>${escapeFiscalHtml(r.expenseDate)}</td><td>${r.invoiceDocType}</td>` +
        `<td>${escapeFiscalHtml(formatComprobanteNumber(r.invoicePv, r.invoiceNumber) ?? '')}</td>` +
        `<td>${escapeFiscalHtml(r.supplierTaxId ?? '')}</td><td>${escapeFiscalHtml(r.supplierName ?? '')}</td>` +
        `<td>${escapeFiscalHtml(formatFiscalMoney(a.neto))}</td>` +
        `<td>${escapeFiscalHtml(formatFiscalMoney(a.total))}</td></tr>`
      );
    })
    .join('');
  return `<table border="1" cellpadding="4" cellspacing="0" width="100%" style="font-size:11px;border-collapse:collapse">
    <thead><tr><th>Fecha</th><th>Tipo</th><th>Cbte</th><th>CUIT</th><th>Proveedor</th><th>Neto</th><th>Total</th></tr></thead>
    <tbody>${body}</tbody></table>`;
}

export function buildLibroIvaControlHtml(input: LibroIvaControlPdfInput): string {
  const branch = input.branchLabel
    ? `<p><strong>Sucursal:</strong> ${escapeFiscalHtml(input.branchLabel)}</p>`
    : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Libro IVA control</title></head>
<body style="font-family:Helvetica,Arial,sans-serif;padding:24px;color:#111">
  <h1 style="font-size:18px">Libro IVA — control mensual</h1>
  <p><strong>Período:</strong> ${escapeFiscalHtml(input.periodLabel)}</p>
  <p><strong>Emisor:</strong> ${escapeFiscalHtml(input.emitterBusinessName)}</p>
  <p><strong>CUIT:</strong> ${escapeFiscalHtml(formatFiscalCuit(input.emitterCuit) ?? input.emitterCuit)}</p>
  ${branch}
  <p style="font-size:10px;color:#666">Control interno. No reemplaza el TXT del Libro IVA Digital (ARCA).</p>
  <h2 style="font-size:14px;margin-top:20px">Ventas fiscales</h2>
  ${ventasTableHtml(input.ventas)}
  <h2 style="font-size:14px;margin-top:20px">Compras fiscales</h2>
  ${comprasTableHtml(input.compras)}
</body></html>`;
}

export async function downloadLibroIvaControlPdf(input: LibroIvaControlPdfInput): Promise<void> {
  const filename = `libro-iva-control-${input.periodTag}.pdf`;

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const { buildLibroIvaControlPdfBlob } = await import('./libroIvaControlPdfWeb');
    const blob = await buildLibroIvaControlPdfBlob(input);
    downloadBlobWeb(filename, blob);
    return;
  }

  const html = buildLibroIvaControlHtml(input);
  const { uri } = await Print.printToFileAsync({
    html,
    base64: false,
    width: 595,
    height: 842,
  });
  await shareNativePdf(uri, 'Guardar libro IVA PDF');
}
